import eventsApi from 'api/requests/events';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import EventProvider from 'context/events/Event';
import date from 'helpers/date';
import { Calendar, ChevronLeft, MapPin, Pencil } from 'lucide-react';
import ProductDetail from 'modules/dashboard/events/detail/products/detail/ProductDetail';
import EventOrders from 'modules/dashboard/events/detail/orders/EventOrders';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const TABS = {
    detail: { key: 'detail', label: 'Visão Geral' },
    products: { key: 'products', label: 'Produtos' },
    orders: { key: 'orders', label: 'Pedidos' },
}

/**
 * Tela do evento pra quem chega por aqui como colaborador (não pelo painel
 * do produtor, que exige papel produtor/estabelecimento/admin). Reaproveita
 * os componentes já usados pelo próprio MainContent.js (ProductDetail,
 * EventOrders) — os dois só pedem o slug do evento e buscam os próprios
 * dados, sem depender do resto do assistente do produtor.
 */
export default function CollaboratorEventDetail({ event, onBack }) {
    const canViewSales = event?.permissions?.includes('event.sales.view');
    const canEdit = event?.permissions?.includes('event.edit');

    const [activeTab, setActiveTab] = useState(TABS.detail.key);
    const [editNoticeVisible, setEditNoticeVisible] = useState(false);
    const [eventDetail, setEventDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getEvent();
    }, [event?.slug]);

    async function getEvent() {
        setLoading(true);

        try {
            const { data } = await eventsApi.getBySlug(event.slug);
            setEventDetail(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const availableTabs = [
        TABS.detail,
        TABS.products,
        ...(canViewSales ? [TABS.orders] : []),
    ];

    const firstDate = eventDetail?.dates?.[0];

    return (
        <EventProvider>
            <Container>
                <Header>
                    <BackButton onClick={onBack}>
                        <ChevronLeft size={20} color={colors.nightDark} />
                    </BackButton>

                    <Text name='highlight' color={colors.nightDark} paddingBottom={0} ellipsisAt={'90%'}>
                        {event?.name}
                    </Text>

                    {
                        canEdit && (
                            <EditButton onClick={() => setEditNoticeVisible(true)}>
                                <Pencil size={13} />
                                Editar evento
                            </EditButton>
                        )
                    }
                </Header>

                <TabsBar>
                    {
                        availableTabs.map(tab => (
                            <TabButton
                                key={tab.key}
                                active={activeTab === tab.key}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </TabButton>
                        ))
                    }
                </TabsBar>

                <Content>
                    {
                        activeTab === TABS.detail.key && (
                            loading ? (
                                <FullyCentered height='200px'>
                                    <Loader />
                                </FullyCentered>
                            ) : (
                                <OverviewCard>
                                    {
                                        firstDate?.date && (
                                            <OverviewRow>
                                                <Calendar size={16} color={panelAccent(colors.occasionalPurple)} />
                                                <Text color={colors.nightDark} paddingBottom={0}>
                                                    {date.format(firstDate.date)}
                                                    {firstDate.start ? ` às ${firstDate.start.slice(0, 5)}` : ''}
                                                </Text>
                                            </OverviewRow>
                                        )
                                    }

                                    {
                                        eventDetail?.address?.name && (
                                            <OverviewRow>
                                                <MapPin size={16} color={panelAccent(colors.occasionalPurple)} />
                                                <Text color={colors.nightDark} paddingBottom={0}>
                                                    {eventDetail.address.name}
                                                </Text>
                                            </OverviewRow>
                                        )
                                    }

                                    {
                                        eventDetail?.description && (
                                            <Text color={colors.night} marginTop='14px'>
                                                {eventDetail.description}
                                            </Text>
                                        )
                                    }
                                </OverviewCard>
                            )
                        )
                    }

                    {activeTab === TABS.products.key && <ProductDetail data={event?.slug} />}

                    {activeTab === TABS.orders.key && canViewSales && <EventOrders data={event?.slug} />}
                </Content>

                <Modal isOpen={editNoticeVisible} closeButton size='small' onClose={() => setEditNoticeVisible(false)}>
                    <Column paddingTop='.5rem' paddingBottom='.5rem' paddingLeft='.5rem' paddingRight='.5rem'>
                        <Text name='highlight' color={colors.nightDark}>
                            Edição do evento
                        </Text>
                        <Text marginTop='10px' color={colors.night}>
                            A edição completa do evento ainda não está disponível por aqui. Fale com o produtor pra alterar os dados desse evento.
                        </Text>
                    </Column>
                </Modal>
            </Container>
        </EventProvider>
    );
}

const Container = styled(Column)`
    width: 100%;
`;

const Header = styled(Row)`
    width: 100%;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;

    & > p {
        flex: 1;
        padding-bottom: 0;
    }
`;

const BackButton = styled.button`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${colors.neutralDark};
    border-radius: 50%;
    background: ${colors.neutralLight};
    cursor: pointer;
    padding: 0;
`;

const EditButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 8px 14px;
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '44')};
    border-radius: 8px;
    background: transparent;
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: ${panelAccentAlpha(colors.occasionalPurple, '0D')};
    }
`;

const TabsBar = styled(Row)`
    width: 100%;
    gap: 6px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 16px;
    overflow-x: auto;
`;

const TabButton = styled.button`
    padding: 10px 14px;
    border: none;
    background: transparent;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${({ active }) => active ? panelAccent(colors.occasionalPurple) : colors.night};
    border-bottom: 2px solid ${({ active }) => active ? panelAccent(colors.occasionalPurple) : 'transparent'};
    cursor: pointer;
    white-space: nowrap;
`;

const Content = styled.div`
    width: 100%;
`;

const OverviewCard = styled(Column)`
    width: 100%;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 20px;
    }
`;

const OverviewRow = styled(Row)`
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    & > p {
        padding-bottom: 0;
    }
`;
